import { useCallback, useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import useAssistantPanelStore from "./useStore";

const useSidebarResize = () => {
  const { width, setWidth, setIsSidebarResizing } = useAssistantPanelStore(
    useShallow((state) => ({
      width: state.width,
      setWidth: state.setWidth,
      setIsSidebarResizing: state.setIsSidebarResizing,
    }))
  );

  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const rafId = useRef<number | null>(null);
  const pointerId = useRef<number | null>(null);
  const targetElement = useRef<HTMLElement | null>(null);

  const onStart = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 || e.isPrimary === false) {
        return;
      }

      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      pointerId.current = e.pointerId;
      targetElement.current = target;

      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = width;
      document.body.style.userSelect = "none";
      setIsSidebarResizing(true);
    },
    [width, setIsSidebarResizing]
  );

  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!isResizing.current) return;

      e.preventDefault();

      if (rafId.current) return;

      rafId.current = requestAnimationFrame(() => {
        const delta = e.clientX - startX.current;
        const newWidth = startWidth.current + delta;
        setWidth(newWidth);
        rafId.current = null;
      });
    };

    const handleEnd = () => {
      if (!isResizing.current) return;

      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }

      if (targetElement.current && pointerId.current !== null) {
        targetElement.current.releasePointerCapture(pointerId.current);
        pointerId.current = null;
        targetElement.current = null;
      }

      isResizing.current = false;
      document.body.style.userSelect = "";
      setIsSidebarResizing(false);
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleEnd);
    document.addEventListener("pointercancel", handleEnd);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleEnd);
      document.removeEventListener("pointercancel", handleEnd);
    };
  }, [setWidth, setIsSidebarResizing]);

  return { onStart };
};

export default useSidebarResize;
